// Utility function to get the correct image URL with size variants.
// Handles three URL families:
//   1. Cloudinary URLs            -> inject f_auto,q_auto,w_X transformation
//   2. R2 /v2/ URLs (post-Sprint2) -> swap to pre-generated WebP variant
//   3. Anything else              -> return as-is

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SIZE_TRANSFORMS = {
  thumb: 'f_auto,q_auto,w_200,c_limit',
  card: 'f_auto,q_auto,w_600,c_limit',
  medium: 'f_auto,q_auto,w_1000,c_limit',
  large: 'f_auto,q_auto,w_1600,c_limit',
  default: 'f_auto,q_auto',
};

// Map a logical size to the R2 variant suffix actually generated by
// utils/r2_helper.py::_VARIANT_CONFIGS. `medium`/`large` keep the original.
const R2_VARIANT_MAP = {
  thumb: 'thumb',
  card: 'card',
};

export const getImageUrl = (imagePath, size = 'default') => {
  if (!imagePath) return null;

  // Cloudinary URL → inject transformation
  if (imagePath.includes('res.cloudinary.com')) {
    const transform = SIZE_TRANSFORMS[size] || SIZE_TRANSFORMS.default;
    if (/\/upload\/(f_|w_|q_|c_)/.test(imagePath)) {
      return imagePath;
    }
    return imagePath.replace('/upload/', `/upload/${transform}/`);
  }

  // R2 v2 URL → swap the original extension for the variant WebP
  // Example: https://pub-xxx.r2.dev/services/v2/uuid.jpg
  //       -> https://pub-xxx.r2.dev/services/v2/uuid_thumb.webp
  if (imagePath.includes('/v2/') && (imagePath.includes('r2.dev') || imagePath.includes('r2.cloudflarestorage.com'))) {
    const variant = R2_VARIANT_MAP[size];
    if (variant) {
      // Strip query string, replace the trailing ".<ext>" with "_<variant>.webp"
      const [base, qs] = imagePath.split('?', 2);
      const swapped = base.replace(/\.[a-zA-Z0-9]+$/, `_${variant}.webp`);
      return qs ? `${swapped}?${qs}` : swapped;
    }
    // For 'medium' / 'large' / 'default' just serve the original.
    return imagePath;
  }

  // Already a full URL (Unsplash, external, legacy R2 without /v2/) → leave as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // Local backend path
  return `${BACKEND_URL}${imagePath}`;
};

export default getImageUrl;
